import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { APIProvider, AdvancedMarker, Map, Polygon, Polyline, useMap } from "@vis.gl/react-google-maps";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatIconAdd } from "blocks-nextjs/blocks-react/components/Icons/Add/Add";
import { CatIconCancel } from "blocks-nextjs/blocks-react/components/Icons/Cancel/Cancel";
import { CatIconCancelUndo } from "blocks-nextjs/blocks-react/components/Icons/CancelUndo/CancelUndo";
import { CatIconCloseCircle } from "blocks-nextjs/blocks-react/components/Icons/CloseCircle/CloseCircle";
import { CatIconReorder } from "blocks-nextjs/blocks-react/components/Icons/Reorder/Reorder";
import { CatIconUndo } from "blocks-nextjs/blocks-react/components/Icons/Undo/Undo";
import { CatTextPassage } from "blocks-nextjs/blocks-react/components/TextPassage/TextPassage";
import { CatTooltip } from "blocks-nextjs/blocks-react/components/Tooltip/Tooltip";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getGoogleMapsApiKey } from "../../lib/getEnvVariables";
import useGlobalStore from "../../store/useGlobalStore";
import useNewProjectStore from "../../store/useNewProjectStore";
import useProjectGeoFenceStore from "../../store/useProjectGeoFenceStore";
import { Variant } from "../GlobalNotification/GlobalNotification";
import { checkIsValidPolygon } from "./GeoFence.helper";
import { POLYGON_TYPES } from "./GoogleMap.interface";
import PolygonDrawingManager from "./PolygonDrawingManager";
import "./ProjectGeoFenceMap.css";
const API_KEY = getGoogleMapsApiKey();
const MAP_ID = "project-geofence-map";
const DEFAULT_ZOOM = 13;
const MODE_ADD = "add";
const MODE_EDIT = "edit";
const DRAWING_MODE_POLYGON = "polygon";
const GEOFENCE_TYPE_NONE = "none";
const MIN_POLYGON_POINTS = 3;
const BASE_POLYGON_Z_INDEX = 1000;
const HOVER_LABEL_Z_INDEX = 2000;
// Polygon colors by type
const COLOR_PROJECT_AREA_FILL = "#8CBAFF";
const COLOR_PROJECT_AREA_STROKE = "#0066FF";
const COLOR_PROJECT_AREA_LABEL = "#0078D6";
const COLOR_JOB_SITE = "#D1A0FF";
const COLOR_JOB_SITE_STROKE = "#9B30FF";
const COLOR_CHARGE_STATION = "#18AEA5";
const PROJECT_AREA_FILL_OPACITY = 0.1;
const PROJECT_AREA_STROKE_WEIGHT = 2.44;
const JOB_SITE_FILL_OPACITY = 0.4;
const CHARGE_STATION_FILL_OPACITY = 0.4;
const LEGEND_FILL_ALPHA = "59";
const LEGEND_SWATCH_SIZE = "12px";
const LEGEND_PADDING = "10px";
const LEGEND_CONTROL_ID = "geofence-color-legend";
var PolygonStrokeWeight;
(function (PolygonStrokeWeight) {
    PolygonStrokeWeight[PolygonStrokeWeight["DEFAULT"] = 2] = "DEFAULT";
    PolygonStrokeWeight[PolygonStrokeWeight["EDIT"] = 3] = "EDIT";
    PolygonStrokeWeight[PolygonStrokeWeight["THIN"] = 1] = "THIN";
})(PolygonStrokeWeight || (PolygonStrokeWeight = {}));
// approximate planar (shoelace) area, used only for stacking order so smaller shapes render above larger ones
const getApproxPolygonArea = (paths) => {
    let area = 0;
    for (let i = 0; i < paths.length; i += 1) {
        const current = paths[i];
        const next = paths[(i + 1) % paths.length];
        area += current.lng * next.lat - next.lng * current.lat;
    }
    return Math.abs(area / 2);
};
const getPolygonOptions = (polygonType) => {
    switch (polygonType) {
        case POLYGON_TYPES.PROJECT_AREA:
            return {
                fillColor: COLOR_PROJECT_AREA_FILL,
                fillOpacity: PROJECT_AREA_FILL_OPACITY,
                strokeColor: COLOR_PROJECT_AREA_STROKE,
                strokeOpacity: 0, // Dashed stroke rendered via Polyline
                strokeWeight: PolygonStrokeWeight.DEFAULT
            };
        case POLYGON_TYPES.JOB_SITE:
            return {
                fillColor: COLOR_JOB_SITE,
                fillOpacity: JOB_SITE_FILL_OPACITY,
                strokeColor: COLOR_JOB_SITE_STROKE,
                strokeOpacity: 0.8,
                strokeWeight: PolygonStrokeWeight.THIN
            };
        case POLYGON_TYPES.CHARGE_STATION:
            return {
                fillColor: COLOR_CHARGE_STATION,
                fillOpacity: CHARGE_STATION_FILL_OPACITY,
                strokeColor: COLOR_CHARGE_STATION,
                strokeOpacity: 0.8,
                strokeWeight: PolygonStrokeWeight.THIN
            };
        default:
            return {
                fillColor: COLOR_PROJECT_AREA_FILL,
                fillOpacity: PROJECT_AREA_FILL_OPACITY,
                strokeColor: COLOR_PROJECT_AREA_STROKE,
                strokeOpacity: 0,
                strokeWeight: PolygonStrokeWeight.DEFAULT
            };
    }
};
const getHoverLabelColor = (polygonType) => polygonType === POLYGON_TYPES.PROJECT_AREA ? COLOR_PROJECT_AREA_LABEL : getPolygonOptions(polygonType).fillColor;
const getDashedStrokeIcons = (strokeColor) => [
    {
        icon: {
            path: "M 0,-1 0,1",
            scale: PROJECT_AREA_STROKE_WEIGHT,
            strokeColor,
            strokeOpacity: 1
        },
        offset: "0",
        repeat: "11px"
    }
];
const renderLegend = (map) => {
    const controls = map.controls[google.maps.ControlPosition.LEFT_BOTTOM];
    for (let i = controls.getLength() - 1; i >= 0; i -= 1) {
        const controlNode = controls.getAt(i);
        if (controlNode?.id === LEGEND_CONTROL_ID) {
            controls.removeAt(i);
        }
    }
    const legend = document.createElement("div");
    legend.id = LEGEND_CONTROL_ID;
    legend.style.backgroundColor = "white";
    legend.style.border = "2px solid #fff";
    legend.style.borderRadius = "10px";
    legend.style.boxShadow = "0 2px 6px rgba(0,0,0,.3)";
    legend.style.margin = "10px";
    legend.style.padding = "10px";
    legend.innerHTML = "<h4 style='color:grey'>Color codes</h4>";
    const rows = [
        { label: "Project Area", type: POLYGON_TYPES.PROJECT_AREA },
        { label: "Job Site", type: POLYGON_TYPES.JOB_SITE },
        { label: "Charge Station", type: POLYGON_TYPES.CHARGE_STATION }
    ];
    rows.forEach(({ label, type }) => {
        const options = getPolygonOptions(type);
        const row = document.createElement("div");
        row.className = `flex items-center gap-2 pb-[${LEGEND_PADDING}] text-[12px] font-medium opacity-[0.7]`;
        row.innerHTML = `<div style="border: 1px solid ${options.fillColor}; width: ${LEGEND_SWATCH_SIZE}; height: ${LEGEND_SWATCH_SIZE}; display: inline-block; background: ${options.fillColor}${LEGEND_FILL_ALPHA};"></div> ${label}`;
        legend.appendChild(row);
    });
    controls.push(legend);
};
// Runs inside <Map> so it can access the native google.maps.Map instance via useMap().
const MapController = ({ onIdle }) => {
    const map = useMap();
    useEffect(() => {
        if (!map) {
            return;
        }
        renderLegend(map);
        const updateMapView = () => {
            const center = map.getCenter();
            if (center) {
                onIdle({ coordinates: { lat: center.lat(), lng: center.lng() }, zoom: map.getZoom() ?? DEFAULT_ZOOM });
            }
        };
        const idleListener = map.addListener("idle", updateMapView);
        const zoomListener = map.addListener("zoom_changed", updateMapView);
        return () => {
            idleListener.remove();
            zoomListener.remove();
        };
    }, [map, onIdle]);
    return null;
};
const ProjectGeoFenceMap = () => {
    const { setGlobalNotification } = useGlobalStore((state) => ({
        setGlobalNotification: state.setGlobalNotification
    }));
    const { projectAddressLat, projectAddressLng } = useNewProjectStore((state) => ({
        projectAddressLat: state.projectAddressLat,
        projectAddressLng: state.projectAddressLng
    }));
    const addressCoordinates = { lat: Number(projectAddressLat), lng: Number(projectAddressLng) };
    const { currentSelectedPolygonId, currentSelectedPolygonType, deletePolygonIdInfo, getGeofenceCoordinates, getGeofenceModeType, getGeofenceName, getGeofenceType, projectGeoFenceInfo, resetDeletePolygonId, resetUpdateMapGeoFenceId, setSelectedGeoFenceIdInfo, updateGeoFencePath, updatePolygonId } = useProjectGeoFenceStore((state) => ({
        currentSelectedPolygonId: state.selectedGeoFenceId,
        currentSelectedPolygonType: state.selectedPolygonType,
        deletePolygonIdInfo: state.deletePolygonIdInfo,
        getGeofenceCoordinates: state.getGeofenceCoordinates,
        getGeofenceModeType: state.getGeofenceModeType,
        getGeofenceName: state.getGeofenceName,
        getGeofenceType: state.getGeofenceType,
        projectGeoFenceInfo: state.geofenceList,
        resetDeletePolygonId: state.resetDeletePolygonId,
        resetUpdateMapGeoFenceId: state.resetUpdatePolygonId,
        setSelectedGeoFenceIdInfo: state.setSelectedGeoFenceId,
        updateGeoFencePath: state.setUpdatedGeoFencePath,
        updatePolygonId: state.updatePolygonId
    }));
    const [polygons, setPolygons] = useState([]);
    const [editPolygonId, setEditPolygonId] = useState(null);
    const [hoveredPolygonId, setHoveredPolygonId] = useState(null);
    const [hoverLatLng, setHoverLatLng] = useState(null);
    const [drawingMode, setDrawingMode] = useState(null);
    const [currentPolygonType, setCurrentPolygonType] = useState(POLYGON_TYPES.PROJECT_AREA);
    const [mapView, setMapView] = useState({
        coordinates: addressCoordinates,
        zoom: DEFAULT_ZOOM
    });
    const [history, setHistory] = useState({});
    const [historyIndex, setHistoryIndex] = useState({});
    // Geofence data and transient selection state survive step navigation in the store; reset the
    // whole geofence slice on unmount so re-entering this step re-hydrates fresh from the refetch.
    useEffect(() => {
        return () => {
            useProjectGeoFenceStore.getState?.()?.resetGeofenceState();
        };
    }, []);
    // Sync polygons from the store. Entries without stored coordinates keep their locally drawn
    // path (an invalid edit clears store coordinates to disable saving, but the shape must stay
    // visible); brand-new placeholders with nothing drawn yet are skipped.
    useEffect(() => {
        if (projectGeoFenceInfo && projectGeoFenceInfo.length > 0) {
            setPolygons((prev) => projectGeoFenceInfo
                .map((info) => {
                const hasCoordinates = Array.isArray(info.coordinates) && info.coordinates.length > 0;
                if (!hasCoordinates) {
                    return prev.find((poly) => poly.id === info.geofenceId) ?? null;
                }
                return {
                    id: info.geofenceId,
                    paths: info.coordinates,
                    polygonType: info.geofenceType
                };
            })
                .filter((poly) => poly !== null));
        }
    }, [projectGeoFenceInfo]);
    useEffect(() => {
        if (!currentSelectedPolygonId) {
            setEditPolygonId(null);
            setDrawingMode(null);
            return;
        }
        if (getGeofenceModeType(currentSelectedPolygonId) !== MODE_ADD) {
            setDrawingMode(null);
        }
        if (getGeofenceModeType(currentSelectedPolygonId) === MODE_ADD) {
            setEditPolygonId(null);
            // Re-adding after a cancel keeps the same type value in the store, so the
            // type-change effect never re-fires; auto-start drawing on id change too.
            if (currentSelectedPolygonType !== GEOFENCE_TYPE_NONE && isAddPolygonButtonEnabled()) {
                setDrawingMode(DRAWING_MODE_POLYGON);
            }
        }
    }, [currentSelectedPolygonId, getGeofenceModeType, currentSelectedPolygonType]);
    useEffect(() => {
        if (deletePolygonIdInfo) {
            // Deleting the project area on an active project resets the whole geofence list, so an
            // empty store list means every polygon goes; otherwise (e.g. cancelling a project-area
            // add while other geofences exist) only the deleted polygon is removed.
            const deleteAll = deletePolygonIdInfo.geofenceType === POLYGON_TYPES.PROJECT_AREA &&
                (projectGeoFenceInfo?.length ?? 0) === 0;
            if (deleteAll) {
                setPolygons([]);
            }
            else {
                setPolygons((prev) => prev.filter((poly) => poly.id !== deletePolygonIdInfo.geofenceId));
            }
            setDrawingMode(null);
            setEditPolygonId(null);
            resetDeletePolygonId();
        }
    }, [deletePolygonIdInfo]);
    useEffect(() => {
        if (updatePolygonId) {
            const { newGeofenceId, oldGeoFenceId } = updatePolygonId;
            setPolygons((prev) => prev.map((poly) => (poly.id === oldGeoFenceId ? { ...poly, id: newGeofenceId } : poly)));
            resetUpdateMapGeoFenceId();
        }
    }, [updatePolygonId]);
    useEffect(() => {
        if (currentSelectedPolygonType !== GEOFENCE_TYPE_NONE) {
            setCurrentPolygonType(currentSelectedPolygonType);
            if (getGeofenceModeType(currentSelectedPolygonId) === MODE_ADD) {
                // Auto-start drawing as soon as a geofence type is picked for a new (not-yet-drawn) geofence.
                if (isAddPolygonButtonEnabled()) {
                    setEditPolygonId(null);
                    setDrawingMode(DRAWING_MODE_POLYGON);
                }
                const hasDrawnPolygon = polygons.some((poly) => poly.id === currentSelectedPolygonId);
                const hasCoordinates = (getGeofenceCoordinates(currentSelectedPolygonId)?.length ?? 0) > 0;
                if (hasDrawnPolygon && hasCoordinates) {
                    setPolygons((prev) => prev.map((poly) => poly.id === currentSelectedPolygonId
                        ? { ...poly, polygonType: currentSelectedPolygonType }
                        : poly));
                }
            }
            if (getGeofenceModeType(currentSelectedPolygonId) === MODE_EDIT) {
                setEditPolygonId(null);
                setDrawingMode(null);
            }
        }
    }, [currentSelectedPolygonType]);
    const isAddPolygonButtonEnabled = () => {
        if (!currentSelectedPolygonId) {
            return false;
        }
        const mode = getGeofenceModeType(currentSelectedPolygonId);
        if (mode !== MODE_ADD && mode !== MODE_EDIT) {
            return false;
        }
        const hasDrawnPolygon = polygons.some((poly) => poly.id === currentSelectedPolygonId);
        const hasCoordinates = (getGeofenceCoordinates(currentSelectedPolygonId)?.length ?? 0) > 0;
        return !hasDrawnPolygon && !hasCoordinates;
    };
    const handlePolygonComplete = useCallback((paths) => {
        if (!currentSelectedPolygonId) {
            setDrawingMode(null);
            return;
        }
        if (paths.length < MIN_POLYGON_POINTS) {
            setGlobalNotification({
                message: "Polygon must have at least 3 points.",
                showNotification: true,
                type: Variant.warning
            });
            return;
        }
        const newPolygon = {
            id: currentSelectedPolygonId,
            paths,
            polygonType: currentPolygonType
        };
        const validation = checkIsValidPolygon(newPolygon, polygons);
        setPolygons((prev) => [...prev, newPolygon]);
        setEditPolygonId(null);
        setDrawingMode(null);
        if (validation.isValid) {
            updateGeoFencePath({ coordinates: paths, geofenceId: newPolygon.id });
        }
        else {
            setGlobalNotification({ message: validation.message, showNotification: true, type: Variant.warning });
            updateGeoFencePath({ coordinates: [], geofenceId: newPolygon.id });
        }
    }, [currentSelectedPolygonId, currentPolygonType, polygons, setGlobalNotification, updateGeoFencePath]);
    // Initialize history when polygons are loaded
    useEffect(() => {
        if (polygons.length > 0) {
            const initialHistory = {};
            const initialHistoryIndex = {};
            polygons.forEach((poly) => {
                if (!history[poly.id]) {
                    initialHistory[poly.id] = [poly.paths];
                    initialHistoryIndex[poly.id] = 0;
                }
            });
            if (Object.keys(initialHistory).length > 0) {
                setHistory((prev) => ({ ...prev, ...initialHistory }));
                setHistoryIndex((prev) => ({ ...prev, ...initialHistoryIndex }));
            }
        }
    }, [polygons, history]);
    const updateHistory = useCallback((geofenceId, newPath) => {
        setHistoryIndex((prevIndex) => {
            const currentIndex = prevIndex[geofenceId] ?? -1;
            const nextIndex = currentIndex + 1;
            setHistory((prevHistory) => {
                const currentHistory = prevHistory[geofenceId] || [];
                const newHistory = [...currentHistory.slice(0, currentIndex + 1), newPath];
                return { ...prevHistory, [geofenceId]: newHistory };
            });
            return { ...prevIndex, [geofenceId]: nextIndex };
        });
    }, []);
    const onEdit = useCallback((geofenceId, changedPaths) => {
        const [outerRing] = changedPaths;
        if (!outerRing) {
            return;
        }
        const nextPath = outerRing.map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));
        const current = polygons.find((poly) => poly.id === geofenceId);
        if (!current) {
            return;
        }
        if (JSON.stringify(nextPath) === JSON.stringify(current.paths)) {
            return;
        }
        const validation = checkIsValidPolygon({ ...current, paths: nextPath }, polygons);
        if (validation.isValid) {
            updateHistory(geofenceId, nextPath);
            setPolygons((prev) => prev.map((poly) => (poly.id === geofenceId ? { ...poly, paths: nextPath } : poly)));
            updateGeoFencePath({ coordinates: nextPath, geofenceId });
        }
        else {
            // Match GoogleMapGeoFence: keep the edited path visible but clear the stored
            // coordinates so the form invalidates and disables saving the invalid shape.
            setGlobalNotification({ message: validation.message, showNotification: true, type: Variant.warning });
            setPolygons((prev) => prev.map((poly) => (poly.id === geofenceId ? { ...poly, paths: nextPath } : poly)));
            updateGeoFencePath({ coordinates: [], geofenceId });
        }
    }, [polygons, updateGeoFencePath, setGlobalNotification, updateHistory]);
    const handleUndo = () => {
        if (!editPolygonId)
            return;
        const currentIndex = historyIndex[editPolygonId];
        if (currentIndex > 0) {
            const newIndex = currentIndex - 1;
            const path = history[editPolygonId][newIndex];
            setHistoryIndex((prev) => ({ ...prev, [editPolygonId]: newIndex }));
            setPolygons((prev) => prev.map((poly) => (poly.id === editPolygonId ? { ...poly, paths: path } : poly)));
            updateGeoFencePath({ coordinates: path, geofenceId: editPolygonId });
        }
    };
    const handleRedo = () => {
        if (!editPolygonId)
            return;
        const currentIndex = historyIndex[editPolygonId];
        const currentHistory = history[editPolygonId];
        if (currentIndex < currentHistory.length - 1) {
            const newIndex = currentIndex + 1;
            const path = history[editPolygonId][newIndex];
            setHistoryIndex((prev) => ({ ...prev, [editPolygonId]: newIndex }));
            setPolygons((prev) => prev.map((poly) => (poly.id === editPolygonId ? { ...poly, paths: path } : poly)));
            updateGeoFencePath({ coordinates: path, geofenceId: editPolygonId });
        }
    };
    const handleClearAll = () => {
        if (!editPolygonId)
            return;
        const initialPath = history[editPolygonId]?.[0];
        if (!initialPath)
            return;
        setPolygons((prev) => prev.map((poly) => (poly.id === editPolygonId ? { ...poly, paths: initialPath } : poly)));
        setHistory((prev) => ({ ...prev, [editPolygonId]: [initialPath] }));
        setHistoryIndex((prev) => ({ ...prev, [editPolygonId]: 0 }));
        updateGeoFencePath({ coordinates: initialPath, geofenceId: editPolygonId });
    };
    const hoveredPolygon = polygons.find((poly) => poly.id === hoveredPolygonId) ?? null;
    const hoveredPolygonName = hoveredPolygon ? (getGeofenceName?.(hoveredPolygon.id) ?? "").trim() : "";
    // Polygon components only expose mouseover/mouseout; track the cursor's lat/lng with a native
    // mousemove listener so the hover label can follow the cursor inside the map (fullscreen-safe).
    const trackedPolygonsRef = useRef(new WeakSet());
    const attachHoverTracking = useCallback((poly) => {
        if (!poly || trackedPolygonsRef.current.has(poly)) {
            return;
        }
        trackedPolygonsRef.current.add(poly);
        poly.addListener("mousemove", (e) => {
            if (e.latLng) {
                setHoverLatLng({ lat: e.latLng.lat(), lng: e.latLng.lng() });
            }
        });
    }, []);
    return (_jsxs("div", { className: "w-full h-full relative project-geofence-map", "data-test": "project-geofence-map", children: [_jsx(APIProvider, { apiKey: API_KEY, children: _jsxs(Map, { cameraControl: false, className: "w-full h-full", clickableIcons: false, defaultCenter: mapView.coordinates, gestureHandling: "greedy", mapId: MAP_ID, onClick: () => {
                        if (!drawingMode) {
                            setEditPolygonId(null);
                        }
                    }, rotateControl: false, streetViewControl: false, zoom: mapView.zoom, zoomControl: true, children: [_jsx(MapController, { onIdle: setMapView }), _jsx(AdvancedMarker, { position: addressCoordinates }), _jsx(PolygonDrawingManager, { isDrawing: drawingMode === DRAWING_MODE_POLYGON, onSavePolygon: (polygon) => handlePolygonComplete(polygon.paths), polygonType: currentPolygonType }), [...polygons]
                            // larger shapes first (lower z) so nested/inner polygons always stay clickable,
                            // even while an outer polygon is in edit mode
                            .sort((a, b) => getApproxPolygonArea(b.paths) - getApproxPolygonArea(a.paths))
                            .map((polygon, index) => {
                            const options = getPolygonOptions(polygon.polygonType);
                            const isProjectArea = polygon.polygonType === POLYGON_TYPES.PROJECT_AREA;
                            const isEditing = editPolygonId === polygon.id;
                            const zIndex = BASE_POLYGON_Z_INDEX + index;
                            return (_jsxs(React.Fragment, { children: [_jsx(Polygon, { clickable: drawingMode !== DRAWING_MODE_POLYGON, draggable: drawingMode !== DRAWING_MODE_POLYGON && isEditing, editable: drawingMode !== DRAWING_MODE_POLYGON && isEditing, fillColor: options.fillColor, fillOpacity: options.fillOpacity, onClick: (e) => {
                                            e.domEvent.stopPropagation();
                                            if (drawingMode === DRAWING_MODE_POLYGON)
                                                return;
                                            setEditPolygonId(polygon.id);
                                            setSelectedGeoFenceIdInfo(polygon.id);
                                        }, onMouseOut: () => {
                                            setHoveredPolygonId(null);
                                            setHoverLatLng(null);
                                        }, onMouseOver: (e) => {
                                            setHoveredPolygonId(polygon.id);
                                            if (e.latLng) {
                                                setHoverLatLng({ lat: e.latLng.lat(), lng: e.latLng.lng() });
                                            }
                                        }, onPathsChanged: (paths) => onEdit(polygon.id, paths), paths: polygon.paths, ref: attachHoverTracking, strokeColor: options.strokeColor, strokeOpacity: options.strokeOpacity, strokeWeight: options.strokeWeight, zIndex: zIndex }), isProjectArea && (_jsx(Polyline, { icons: getDashedStrokeIcons(options.strokeColor), path: [...polygon.paths, polygon.paths[0]], strokeOpacity: 0, zIndex: zIndex }))] }, polygon.id));
                        }), hoveredPolygon &&
                            hoveredPolygon.id !== editPolygonId &&
                            hoveredPolygonName !== "" &&
                            hoverLatLng && (_jsx(AdvancedMarker, { position: hoverLatLng, zIndex: HOVER_LABEL_Z_INDEX, children: _jsxs("div", { className: "pointer-events-none inline-flex items-center gap-2 rounded-[16px] px-[8.5px] py-[5.5px] text-white shadow select-none font-['Noto_Sans'] not-italic font-[400] text-[12px] leading-[13px] tracking-[0] whitespace-nowrap", "data-test": "polygon-hover-label", style: { backgroundColor: getHoverLabelColor(hoveredPolygon.polygonType) }, children: [_jsx("svg", { "data-test": `polygon-marker-${hoveredPolygon.id}`, height: "16", viewBox: "0 0 16 16", width: "16", children: _jsx("circle", { cx: "8", cy: "8", fill: "none", r: "7.335", stroke: "#fff", strokeDasharray: "4,4", strokeWidth: "1.33" }) }), hoveredPolygonName] }) }))] }) }), _jsxs("div", { className: "bg-white absolute top-[120px] right-[10px] rounded flex flex-col items-center w-[40px] z-10", children: [drawingMode && (_jsxs(CatTooltip, { allowedPlacements: ["left"], "data-test": "cat-tooltip-cancel-drawing", children: [_jsxs(CatButton, { hideText: true, onClick: () => setDrawingMode(null), size: "md", slot: "trigger", variant: "ghost", children: ["Cancel Drawing", _jsx(CatIconCancel, { slot: "after" })] }), _jsx(CatTextPassage, { size: "sm", children: "Cancel Drawing" })] })), isAddPolygonButtonEnabled() && !drawingMode && (_jsxs(_Fragment, { children: [_jsx("hr", { className: "w-full border-gray-200" }), _jsxs(CatTooltip, { allowedPlacements: ["left"], "data-test": "cat-tooltip-add-polygon", children: [_jsxs(CatButton, { hideText: true, onClick: () => {
                                            const geofenceType = getGeofenceType(currentSelectedPolygonId);
                                            if (!geofenceType || geofenceType === GEOFENCE_TYPE_NONE) {
                                                setGlobalNotification({
                                                    message: "Please select a geofence type to draw.",
                                                    showNotification: true,
                                                    type: Variant.warning
                                                });
                                                return;
                                            }
                                            setEditPolygonId(null);
                                            setCurrentPolygonType(geofenceType);
                                            setDrawingMode(DRAWING_MODE_POLYGON);
                                        }, size: "md", slot: "trigger", variant: getGeofenceType(currentSelectedPolygonId) === GEOFENCE_TYPE_NONE ||
                                            !getGeofenceType(currentSelectedPolygonId)
                                            ? "ghost"
                                            : "primary", children: ["Add Polygon", _jsx(CatIconAdd, { slot: "after" })] }), getGeofenceType(currentSelectedPolygonId) === GEOFENCE_TYPE_NONE ||
                                        !getGeofenceType(currentSelectedPolygonId) ? (_jsx(CatTextPassage, { size: "sm", children: "Select Geofence type to draw polygon" })) : (_jsx(CatTextPassage, { size: "sm", children: "Add Polygon" }))] })] })), editPolygonId && !drawingMode && (_jsxs(_Fragment, { children: [_jsxs(CatTooltip, { allowedPlacements: ["left"], "data-testid": "cat-tooltip-undo", children: [_jsxs(CatButton, { disabled: !editPolygonId || (historyIndex[editPolygonId] ?? 0) <= 0, hideText: true, onClick: handleUndo, size: "md", slot: "trigger", variant: "ghost", children: ["Undo", _jsx(CatIconUndo, { slot: "after" })] }), _jsx(CatTextPassage, { size: "sm", children: "Undo" })] }), _jsx("hr", { className: "w-full border-gray-200" }), _jsxs(CatTooltip, { allowedPlacements: ["left"], "data-testid": "cat-tooltip-redo", children: [_jsxs(CatButton, { disabled: !editPolygonId ||
                                            (historyIndex[editPolygonId] ?? 0) >= (history[editPolygonId]?.length ?? 0) - 1, hideText: true, onClick: handleRedo, size: "md", slot: "trigger", variant: "ghost", children: ["Redo", _jsx(CatIconCancelUndo, { slot: "after" })] }), _jsx(CatTextPassage, { size: "sm", children: "Redo" })] }), _jsx("hr", { className: "w-full border-gray-200" }), _jsxs(CatTooltip, { allowedPlacements: ["left"], "data-testid": "cat-tooltip-cancel-all", children: [_jsxs(CatButton, { disabled: !editPolygonId || (historyIndex[editPolygonId] ?? 0) <= 0, hideText: true, onClick: handleClearAll, size: "md", slot: "trigger", variant: "ghost", children: ["Cancel All", _jsx(CatIconCancel, { slot: "after" })] }), _jsx(CatTextPassage, { size: "sm", children: "Cancel All" })] }), _jsx("hr", { className: "w-full border-gray-200" }), _jsxs(CatTooltip, { allowedPlacements: ["left"], "data-test": "cat-tooltip-redraw-polygon", children: [_jsxs(CatButton, { disabled: !editPolygonId, hideText: true, onClick: () => {
                                            if (!editPolygonId)
                                                return;
                                            updateGeoFencePath({ coordinates: [], geofenceId: editPolygonId });
                                            setPolygons((prev) => prev.filter((poly) => poly.id !== editPolygonId));
                                            setEditPolygonId(null);
                                            setCurrentPolygonType((getGeofenceType(currentSelectedPolygonId) ?? currentPolygonType));
                                            setDrawingMode(DRAWING_MODE_POLYGON);
                                        }, size: "md", slot: "trigger", variant: "ghost", children: ["Redraw Polygon", _jsx(CatIconReorder, { slot: "after" })] }), _jsx(CatTextPassage, { size: "sm", children: "Redraw Polygon" })] }), _jsx("hr", { className: "w-full border-gray-200" }), _jsxs(CatTooltip, { allowedPlacements: ["left"], "data-test": "cat-tooltip-close", children: [_jsxs(CatButton, { "data-test": "editing-options-close-button", disabled: !editPolygonId, hideText: true, onClick: () => setEditPolygonId(null), size: "md", slot: "trigger", variant: "ghost", children: ["Close", _jsx(CatIconCloseCircle, { slot: "after" })] }), _jsx(CatTextPassage, { size: "sm", children: "Close" })] })] }))] })] }));
};
export default ProjectGeoFenceMap;
